import { z } from 'zod';

/**
 * #679 9.13 — aceite dos Termos de Uso e da Política de Privacidade.
 *
 * A versão vigente mora em `/appConfig/terms` (rules: leitura pública,
 * escrita só servidor), e é a mesma que o gate de primeiro login dos apps
 * (F4) mostra e manda de volta ao aceitar. Gravar a versão junto do aceite é
 * o que faz um aceite antigo NÃO valer pelos termos novos: quando o jurídico
 * publicar outra versão, basta trocá-la aqui e o gate volta a aparecer.
 */
export const TERMS_CONFIG_DOC_ID = 'terms';

export const TermsConfigSchema = z.object({
  version: z.string().trim().min(1).max(40),
  updatedAt: z.number().int().nonnegative().nullable().optional(),
});
export type TermsConfig = z.infer<typeof TermsConfigSchema>;

/**
 * `setTermsVersion` (super-admin ROOT): a única porta de escrita de
 * `/appConfig/terms`. Trocar a versão é dizer "os aceites anteriores não valem
 * mais para os termos de agora" — quando o gate de primeiro login dos apps
 * existir (F4), todo mundo volta a ver os termos. Por isso o motivo é
 * obrigatório e a ação é auditada.
 */
export const SetTermsVersionInput = z.object({
  version: TermsConfigSchema.shape.version,
  reason: z.string().trim().min(3).max(200),
});
export type SetTermsVersionInput = z.infer<typeof SetTermsVersionInput>;

export type SetTermsVersionResult = {
  version: string;
  /** A versão que valia antes; `null` quando o documento ainda não existia. */
  previousVersion: string | null;
};

/**
 * `acceptTerms`: o cliente manda a versão que MOSTROU. Se ela não é a
 * vigente, o aceite é recusado — senão alguém com a tela aberta desde ontem
 * aceitaria hoje, em nome da versão nova, um texto que não leu.
 */
export const AcceptTermsInput = z.object({
  version: z.string().trim().min(1).max(40),
});
export type AcceptTermsInput = z.infer<typeof AcceptTermsInput>;

export const AcceptTermsOutput = z.object({
  termsAcceptedAt: z.number().int().nonnegative(),
  termsVersion: z.string(),
});
export type AcceptTermsOutput = z.infer<typeof AcceptTermsOutput>;

/**
 * Gate de primeiro login (#679 9.13): o usuário precisa aceitar os termos?
 *
 * Um predicado só, espelhado no Android e no iOS, com a mesma tabela de casos:
 *  - sem versão vigente (`current === null`, documento ausente ou inválido) →
 *    **não** bloqueia: não há o que aceitar, e o `acceptTerms` recusaria. A
 *    ausência é reportada por quem chama — é defeito de configuração, não do
 *    usuário;
 *  - versão aceita diferente da vigente (inclusive nunca aceita) → bloqueia.
 */
export function needsTermsAcceptance(
  acceptedVersion: string | null | undefined,
  current: TermsConfig | null,
): boolean {
  if (current === null) return false;
  return acceptedVersion !== current.version;
}
